import { useCallback, useState } from 'react';
import { ControlProps } from '@jsonforms/core';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Refresh from '@mui/icons-material/Refresh';
import { withJsonFormsControlProps, useJsonForms } from '@jsonforms/react';
import { useEndpointControl } from '../shared/useEndpointControl';
import './UseEndpointTextControl.styles.scss';

const SECRET_PROPERTY_NAMES = ['cr_token', 'passphrase'];
const SECRET_LABEL_PATTERN = /token|passphrase|password|onepass/i;

/**
 * Text control for fields with use_endpoint (fetch-from-endpoint button).
 * Registered with a tester that matches string + use_endpoint so it only runs for those fields.
 */
export const UseEndpointTextControl = withJsonFormsControlProps((props: ControlProps) => {
    const { data, handleChange, path, label, required, errors, schema, uischema } = props;
    const [showPassword, setShowPassword] = useState(false);
    const ctx = useJsonForms();
    const formData = ctx?.core?.data;

    const schemaTitle = (schema as { title?: string })?.title ?? '';

    const {
        propertyName,
        showError,
        helperText,
        handleFetchClick,
        isFetching,
        hasUseEndpoint,
    } = useEndpointControl({
        path,
        uischema,
        schema,
        errors,
        handleChange,
        formData,
    });

    const scope = typeof uischema === 'object' && uischema != null && 'scope' in uischema ? (uischema as { scope?: string }).scope : undefined;
    const isSecretField =
        schema?.format === 'password' ||
        SECRET_PROPERTY_NAMES.includes(propertyName) ||
        (path?.includes('cr_token') || scope?.includes('cr_token') || path?.includes('passphrase') || scope?.includes('passphrase')) ||
        SECRET_LABEL_PATTERN.test(label ?? '') ||
        SECRET_LABEL_PATTERN.test(schemaTitle);

    const handleTextFieldChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            handleChange(path, e.target.value);
        },
        [handleChange, path]
    );

    const inputType = isSecretField ? (showPassword ? 'text' : 'password') : 'text';

    const endAdornment = (isSecretField || hasUseEndpoint) ? (
        <InputAdornment position="end">
            {hasUseEndpoint && (
                <IconButton
                    aria-label="Fetch value from endpoint"
                    onClick={handleFetchClick}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                    size="small"
                    disabled={isFetching}
                >
                    <Refresh fontSize="small" />
                </IconButton>
            )}
            {isSecretField && (
                <IconButton
                    aria-label={showPassword ? 'Hide value' : 'Show value'}
                    onClick={() => setShowPassword((p) => !p)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                    size="small"
                >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
            )}
        </InputAdornment>
    ) : undefined;

    return (
        <div className="rpc-use-endpoint-text-control">
            <TextField
                value={data || ''}
                onChange={handleTextFieldChange}
                label={label}
                required={required}
                error={showError}
                helperText={helperText}
                variant="outlined"
                fullWidth
                type={inputType}
                autoComplete={isSecretField ? 'off' : undefined}
                slotProps={{
                    input: {
                        required: false,
                        endAdornment,
                    },
                    htmlInput: {
                        type: inputType,
                        autoComplete: isSecretField ? 'off' : undefined,
                    },
                }}
            />
        </div>
    );
});
